import { Component, Inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { DOCUMENT } from '@angular/common';
import { HERBS } from '../../data/herbs';
import { Herb } from '../../models/herb.model';
import { RouterLink } from '@angular/router';
import { SeoService } from '../../services/seo.service';
import { ImageService } from '../../services/image.service';

@Component({
  selector: 'app-saisonkalender',
  standalone: true,
  imports: [CommonModule, NgOptimizedImage, RouterLink],
  templateUrl: './saisonkalender.component.html',
  styleUrls: ['./saisonkalender.component.css']
})
export class SaisonkalenderComponent implements OnInit, OnDestroy {
  // Importiere Kräuterdaten
  herbs: Herb[] = HERBS;

  // Monatsnamen
  months: string[] = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];

  // Aktuell *ausgewählter* Monat für die Anzeige (0-basiert)
  currentMonthIndex: number = new Date().getMonth();

  // NEU: Tatsächlicher aktueller Monat (0-basiert) für Hervorhebung
  actualCurrentMonthIndex: number = new Date().getMonth();


  // Aktuell angezeigte Kräuter (basierend auf Ernte im currentMonthIndex)
  seasonalHerbs: Herb[] = [];

  // Aktuell ausgewähltes Kraut für Detail-Ansicht
  selectedHerb: Herb | null = null;
  showDetailPopup: boolean = false;

  constructor(
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,

    private seoService: SeoService,
    public imageService: ImageService
  ) {}

  ngOnInit(): void {
    const title = 'Kräuter Saisonkalender'
    const description = 'Entdecke in unserem saisonalen Kräuterkalender, wann heimische Küchen- und Heilkräuter wachsen, wie du sie erkennst und richtig anwendest – Monat für Monat im Überblick.'
    this.seoService.setTitle(title);
    this.seoService.setDescription(description);
    this.seoService.setOpenGraphTags({
      title: title,
      description: description,
      image: this.seoService.getDefaultImageUrl()
    });
    this.seoService.setCanonicalUrl()
    this.seoService.setBreadcrumbSchema([
      {name: 'Startseite', url: 'https://kraeutersaat.de'},
      {name: title, url: `https://kraeutersaat.de/kraeuter-saisonkalender`},
    ]);


    this.updateSeasonalHerbs();
  }

  ngOnDestroy(){
    this.renderer.removeClass(this.document.documentElement, 'modal-open');
  }

  // Navigiert zum vorherigen Monat
  prevMonth(): void {
    this.currentMonthIndex = (this.currentMonthIndex - 1 + 12) % 12;
    this.updateSeasonalHerbs();
  }

  // Navigiert zum nächsten Monat
  nextMonth(): void {
    this.currentMonthIndex = (this.currentMonthIndex + 1) % 12;
    this.updateSeasonalHerbs();
  }


  // Aktualisiert die Liste der saisonalen Kräuter basierend auf dem *ausgewählten* Monat
  updateSeasonalHerbs(): void {
    // Filtere Kräuter, die im aktuell ausgewählten Monat geerntet werden können
    const filteredHerbs = this.herbs.filter(herb =>
      this.isHarvestable(herb, this.currentMonthIndex)
    );

    // Sortiere: Hauptsaison-Kräuter zuerst, dann alphabetisch
    this.seasonalHerbs = filteredHerbs.sort((a, b) => {
      // Prüfe Hauptsaison im *aktuell ausgewählten* Monat
      const aIsPeak = a.peakMonths?.includes(this.currentMonthIndex + 1);
      const bIsPeak = b.peakMonths?.includes(this.currentMonthIndex + 1);

      if (aIsPeak && !bIsPeak) return -1;
      if (!aIsPeak && bIsPeak) return 1;
      return a.name.localeCompare(b.name);
    });
  }

  // Zeigt Details zu einem Kraut an
  showHerbDetail(herb: Herb): void {
    this.selectedHerb = herb;
    this.showDetailPopup = true;

    // Scrollen im Hintergrund verhindern
    this.renderer.addClass(this.document.documentElement, 'modal-open');
  }

  // Schließt das Detail-Popup
  closeDetailPopup(): void {
    this.showDetailPopup = false;
    this.selectedHerb = null;

    // Scrollen im Hintergrund wieder ermöglichen
    this.renderer.removeClass(this.document.documentElement, 'modal-open');
  }


  // Überprüft, ob ein Kraut im *aktuell ausgewählten* Monat in der Hauptsaison ist
  isInPeakSeason(herb: Herb): boolean {
    return herb.peakMonths?.includes(this.currentMonthIndex + 1) || false;
  }

  // Überprüft, ob ein Kraut im gegebenen Monat (Index 0-11) erntbar ist
  isHarvestable(herb: Herb, monthIndex: number): boolean {
    return herb.harvestMonths?.includes(monthIndex + 1) || false;
  }

  // Generiert eine Farbe für ein Kraut, falls keine definiert ist
  getHerbColor(herb: Herb): string {
    // Fallback-Farbe, falls colorHex nicht gesetzt ist
    return herb.colorHex || `hsl(${(herb.id.length * 30 + herb.name.charCodeAt(0)) % 360}, 60%, 70%)`;
  }

  // Gibt die Erntezeit eines Krauts als Text zurück
  getHarvestTimeText(herb: Herb): string {
    if (!herb.harvestMonths?.length) {
      return 'Keine spezifischen Erntezeiten verfügbar.';
    }
    const harvestText = `Ernte: ${this.getMonthRangeText(herb.harvestMonths)}`;
    const peakText = herb.peakMonths?.length ? `Hauptsaison: ${this.getMonthRangeText(herb.peakMonths)}` : '';
    return peakText ? `${harvestText} (${peakText}).` : `${harvestText}.`;
  }

  // Gibt die Verwendungen als String zurück - für beide Formate (string/string[]) kompatibel
  getUsesText(herb: Herb): string {
    if (typeof herb.uses === 'string') {
      return herb.uses;
    } else if (Array.isArray(herb.uses)) {
      return herb.uses.join(', ');
    }
    return 'Keine Angaben zur Verwendung.';
  }

  // Prüft, ob überhaupt Pflanzdaten vorhanden sind
  hasPlantingData(herb: Herb): boolean {
    return !!(herb.sowingIndoorsMonths?.length || herb.sowingOutdoorsMonths?.length || herb.plantingOutMonths?.length);
  }

  // Prüft, ob Vorkultur-Daten vorhanden sind
  hasSowingIndoors(herb: Herb): boolean {
    return !!herb.sowingIndoorsMonths?.length;
  }

  // Prüft, ob Außenaussaat-Daten vorhanden sind
  hasSowingOutdoors(herb: Herb): boolean {
    return !!herb.sowingOutdoorsMonths?.length;
  }

  // Prüft, ob Auspflanzen-Daten vorhanden sind
  hasPlantingOut(herb: Herb): boolean {
    return !!herb.plantingOutMonths?.length;
  }

  // Prüft, ob im gegebenen Monat (Index 0-11) drinnen gesät werden kann
  isSowableIndoors(herb: Herb, monthIndex: number): boolean {
    return herb.sowingIndoorsMonths?.includes(monthIndex + 1) || false;
  }

  // Prüft, ob im gegebenen Monat (Index 0-11) draußen gesät werden kann
  isSowableOutdoors(herb: Herb, monthIndex: number): boolean {
    return herb.sowingOutdoorsMonths?.includes(monthIndex + 1) || false;
  }

  // Prüft, ob im gegebenen Monat (Index 0-11) ausgepflanzt werden kann
  isPlantableOut(herb: Herb, monthIndex: number): boolean {
    return herb.plantingOutMonths?.includes(monthIndex + 1) || false;
  }

  // Generiert Text für die Pflanzzeiten
  getPlantingTimeText(herb: Herb): string {
    if (!this.hasPlantingData(herb)) {
      return ''; // Kein Text, wenn keine Daten da sind
    }

    let textParts: string[] = [];
    if (herb.sowingIndoorsMonths?.length) {
      textParts.push(`Vorkultur: ${this.getMonthRangeText(herb.sowingIndoorsMonths)}`);
    }
    if (herb.sowingOutdoorsMonths?.length) {
      textParts.push(`Direktsaat: ${this.getMonthRangeText(herb.sowingOutdoorsMonths)}`);
    }
    if (herb.plantingOutMonths?.length) {
      textParts.push(`Auspflanzen: ${this.getMonthRangeText(herb.plantingOutMonths)}`);
    }

    return textParts.join('; ');
  }
  

  // Hilfsfunktion zur Erstellung von Monatsbereichen (z.B. "März - Mai")
  private getMonthRangeText(monthNumbers: number[] | undefined): string {
    if (!monthNumbers || monthNumbers.length === 0) return '';
    // Sicherstellen, dass die Nummern gültig sind (1-12)
    const validMonths = monthNumbers.filter(m => m >= 1 && m <= 12);
    if (validMonths.length === 0) return '';

    const sortedMonths = [...validMonths].sort((a, b) => a - b);
    const firstMonth = this.months[sortedMonths[0] - 1];
    const lastMonth = this.months[sortedMonths[sortedMonths.length - 1] - 1];

    // Erzeugt eine lesbare Liste, wenn Monate nicht zusammenhängend sind
    if (sortedMonths.length > 1 && sortedMonths[sortedMonths.length - 1] - sortedMonths[0] !== sortedMonths.length - 1) {
        // Prüfe auf Lücken - einfache Variante: einfach alle Monate auflisten
        return sortedMonths.map(m => this.months[m - 1]).join(', ');
    }

    return sortedMonths.length === 1 ? firstMonth : `${firstMonth} - ${lastMonth}`;
  }
}